import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../firebase';
import {
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signInWithPopup,
    GoogleAuthProvider,
    signOut,
    onAuthStateChanged,
    sendPasswordResetEmail
} from 'firebase/auth';
import api from '../utils/api';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);

   const API_URL = process.env.REACT_APP_API_URL || "https://csp-2-3.onrender.com";


    // Set token in localStorage
    useEffect(() => {
        if (token) {
            localStorage.setItem('token', token);
        } else {
            localStorage.removeItem('token');
        }
    }, [token]);

    // Exchange Firebase token for JWT
    const exchangeFirebaseToken = async (firebaseUser) => {
        try {
            const idToken = await firebaseUser.getIdToken();
            const response = await api.post('/api/auth/login', { idToken });

            if (response.data.success) {
                setToken(response.data.token);
                setCurrentUser(response.data.user);
                return response.data;
            }
        } catch (error) {
            console.error('Token exchange failed:', error);
            throw error;
        }
    };

    // Listen to Firebase auth state
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                try {
                    // Only exchange if we don't have a user or the UID changed
                    if (!currentUser || currentUser.uid !== firebaseUser.uid) {
                        await exchangeFirebaseToken(firebaseUser);
                    }
                } catch (error) {
                    console.error('Auth state error:', error);
                    setCurrentUser(null);
                    setToken(null);
                }
            } else {
                setCurrentUser(null);
                setToken(null);
            }
            setLoading(false);
        });

        return unsubscribe;
    }, []);

    // Login with email/password
    const login = async (email, password) => {
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const result = await exchangeFirebaseToken(userCredential.user);
            return result;
        } catch (error) {
            console.error('Login error:', error);
            throw error;
        }
    };

    // Register with email/password
    const register = async (email, password) => {
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const result = await exchangeFirebaseToken(userCredential.user);
            return result;
        } catch (error) {
            console.error('Register error:', error);
            throw error;
        }
    };

    // Login with Google
    const loginWithGoogle = async () => {
        try {
            const provider = new GoogleAuthProvider();
            const userCredential = await signInWithPopup(auth, provider);
            // Ensure we exchange immediately so the component gets the result
            return await exchangeFirebaseToken(userCredential.user);
        } catch (error) {
            console.error('Google login error:', error);
            throw error;
        }
    };

    // Reset Password
    const resetPassword = async (email) => {
        try {
            await sendPasswordResetEmail(auth, email);
        } catch (error) {
            console.error('Reset password error:', error);
            throw error;
        }
    };

    // Logout
    const logout = async () => {
        try {
            await signOut(auth);
            await api.post('/api/auth/logout');
            setCurrentUser(null);
            setToken(null);
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    // Verify token
    const verifyToken = async () => {
        if (!token) return false;

        try {
            const response = await api.get('/api/auth/verify');
            return response.data.success;
        } catch (error) {
            setToken(null);
            setCurrentUser(null);
            return false;
        }
    };

    const value = {
        currentUser,
        token,
        login,
        register,
        loginWithGoogle,
        logout,
        verifyToken,
        resetPassword,
        loading
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
